import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { FolderKanban, ClipboardCheck, AlertTriangle, FileText } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_META = [
  { key: 'baixa', name: 'Baixa', color: 'hsl(var(--chart-4))' },
  { key: 'média', name: 'Média', color: 'hsl(var(--chart-5))' },
  { key: 'alta', name: 'Alta', color: 'hsl(var(--chart-2))' },
  { key: 'crítica', name: 'Crítica', color: 'hsl(var(--destructive))' },
];

export const ProjectOverview = () => {
  const { selectedProject } = useApp();
  const [kpis, setKpis] = useState(null);
  const [severity, setSeverity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedProject) {
      fetchData();
    }
    // eslint-disable-next-line
  }, [selectedProject]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [kpisRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/reports/kpis/${selectedProject.id}`),
        axios.get(`${API_URL}/api/heuristics/stats/${selectedProject.id}`).catch(() => ({ data: null })),
      ]);
      setKpis(kpisRes.data);
      setSeverity(statsRes.data?.severity_distribution || null);
    } catch (error) {
      console.error('Error fetching project overview:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedProject) {
    return (
      <div className="flex flex-col items-center justify-center h-96" data-testid="no-project-selected">
        <FolderKanban size={64} className="text-muted-foreground mb-4" />
        <h2 className="text-2xl font-bold mb-2">Nenhum projeto selecionado</h2>
        <p className="text-muted-foreground">Selecione um projeto no menu acima ou crie um novo.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const severityData = SEVERITY_META.map((s) => ({
    name: s.name,
    value: severity ? (severity[s.key] || 0) : 0,
    fill: s.color,
  }));

  return (
    <div className="space-y-6" data-testid="project-overview">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">Visão do projeto</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Indicadores de: <span className="font-medium text-foreground">{selectedProject.name}</span>
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card data-testid="total-projects-card" className="bg-card border-border hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total de Projetos</CardTitle>
            <FolderKanban className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{kpis?.total_projects || 0}</div>
            <p className="text-xs text-muted-foreground">Projetos ativos</p>
          </CardContent>
        </Card>

        <Card data-testid="evaluations-in-progress-card" className="bg-card border-border hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Avaliações em Andamento</CardTitle>
            <ClipboardCheck className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{kpis?.evaluations_in_progress || 0}</div>
            <p className="text-xs text-muted-foreground">Avaliações ativas</p>
          </CardContent>
        </Card>

        <Card data-testid="critical-issues-card" className="bg-card border-border hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Issues Críticas</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{severity ? (severity['crítica'] || 0) : (kpis?.critical_issues || 0)}</div>
            <p className="text-xs text-muted-foreground">Requerem atenção imediata</p>
          </CardContent>
        </Card>

        <Card data-testid="analyses-count-card" className="bg-card border-border hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Análises Técnicas</CardTitle>
            <FileText className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{kpis?.recent_analyses?.length || 0}</div>
            <p className="text-xs text-muted-foreground">Documentadas</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Distribuição de Issues por Severidade</CardTitle>
            <CardDescription>Contagem real das issues das avaliações heurísticas</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={severityData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                <YAxis allowDecimals={false} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: 'hsl(var(--popover-foreground))' }} />
                <Legend />
                <Bar dataKey="value" name="Quantidade" radius={[4, 4, 0, 0]} data-testid="severity-bar">
                  {severityData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Análises Recentes</CardTitle>
            <CardDescription>Últimas análises técnicas criadas</CardDescription>
          </CardHeader>
          <CardContent>
            {kpis?.recent_analyses && kpis.recent_analyses.length > 0 ? (
              <div className="space-y-4">
                {kpis.recent_analyses.slice(0, 5).map((analysis) => (
                  <div key={analysis.id} className="flex items-start gap-3 p-3 rounded-lg border hover:bg-accent transition-colors">
                    <FileText className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{analysis.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {analysis.author_name} • {new Date(analysis.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText size={48} className="mx-auto mb-2 opacity-50" />
                <p>Nenhuma análise técnica encontrada</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
